// The teams list (A5U14), owned by hand: the loader warms the list through the same queryOptions the screen reads,
// prefetched, so a failed list errors only its part (D193).
import { ids } from '@orca/api-client/testIds'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { LockedAction } from '../../../../../components/ui/locked-action'
import { PageHeader } from '../../../../../components/ui/page-header'
import { hasFeature, PeopleTabs } from '../../../../../features/members/PeopleTabs'
import { teamsQuery } from '../../../../../features/teams/queries'
import '../../../../../features/teams/strings'
import { useQuery } from '@tanstack/react-query'
import { TeamsList } from '../../../../../features/teams/TeamsList'
import { accessQuery, can, useCan } from '../../../../../shared/access/access'
import { Part } from '../../../../../shared/shell/StatePart'

export const Route = createFileRoute('/o/$orgSlug/people/teams/')({
  staticData: { screenId: 'TeamsListScreen', access: 'team.read + feature teams' },
  loader: async ({ context: { queryClient, membership } }) => {
    // Without team.read the screen shows its refusal and asks for no teams: the access answer decides.
    const access = await queryClient.ensureQueryData(accessQuery(membership.orgId)).catch(() => null)
    if (can(access, 'team.read') && hasFeature(access?.features, 'teams'))
      await queryClient.prefetchQuery(teamsQuery(membership.orgId))
    return { orgId: membership.orgId }
  },
  component: TeamsListScreen,
})

function TeamsListScreen() {
  const { t } = useTranslation()
  const { orgId } = Route.useLoaderData()
  const { orgSlug } = Route.useParams()
  const canRead = useCan(orgId, 'team.read')
  const { data: access } = useQuery(accessQuery(orgId))
  const inPlan = hasFeature(access?.features, 'teams')
  return (
    <main data-testid={ids.teamsList.root} className="flex flex-col gap-(--stack-lg)">
      <PageHeader title={t('members.people.title')} />
      <PeopleTabs orgId={orgId} orgSlug={orgSlug} current="teams" />
      {access && !inPlan ? (
        <LockedAction
          label={t('teams.create.open')}
          reason={t('members.people.teamsNotInPlan')}
          linkLabel={can(access, 'plan.read') ? t('members.invite.seePlan') : undefined}
          href={can(access, 'plan.read') ? `/o/${orgSlug}/organization/plan` : undefined}
          testId={ids.teamsList.createButton}
          className="max-w-sm"
        />
      ) : canRead ? (
        <Part testId={ids.teamsList.skeleton}>
          <TeamsList orgId={orgId} orgSlug={orgSlug} />
        </Part>
      ) : (
        <p role="alert">{t('teams.detail.refused')}</p>
      )}
    </main>
  )
}
